/** Pre-recorded Deepgram transcription (file upload). */

const ACCEPT =
  "audio/*,video/mp4,video/webm,.mp3,.m4a,.wav,.ogg,.webm,.aac,.flac,.mp4,.mpeg,.mpga";

export const VOICE_UPLOAD_ACCEPT = ACCEPT;

const MAX_BYTES = 200 * 1024 * 1024; // ~200MB — ~20+ min at common bitrates

let cachedKey = null;
let cachedAt = 0;
const KEY_TTL_MS = 10 * 60 * 1000;
const TOKEN_TIMEOUT_MS = 12000;
const TRANSCRIBE_TIMEOUT_MS = 120000;

async function fetchWithTimeout(input, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const externalSignal = options.signal;
  const abortExternal = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", abortExternal, { once: true });
  }
  try {
    return await fetch(input, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener("abort", abortExternal);
  }
}

export async function getDeepgramKey() {
  if (process.env.REACT_APP_DEEPGRAM_API_KEY) {
    return process.env.REACT_APP_DEEPGRAM_API_KEY;
  }
  if (cachedKey && Date.now() - cachedAt < KEY_TTL_MS) return cachedKey;
  const response = await fetchWithTimeout("/api/deepgram-token", {}, TOKEN_TIMEOUT_MS);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "No se pudo obtener la clave de Deepgram");
  }
  const data = await response.json();
  if (!data.key) throw new Error("Deepgram no devolvió API key");
  cachedKey = data.key;
  cachedAt = Date.now();
  return cachedKey;
}

export function isAudioUploadFile(file) {
  if (!file) return false;
  if (file.type?.startsWith("audio/")) return true;
  if (file.type === "video/mp4" || file.type === "video/webm") return true;
  return /\.(mp3|m4a|wav|ogg|webm|aac|flac|mp4|mpeg|mpga|opus)$/i.test(file.name || "");
}

export function validateVoiceFile(file) {
  if (!isAudioUploadFile(file)) {
    return `Formato no soportado: ${file.name}`;
  }
  if (file.size > MAX_BYTES) {
    return `${file.name} es demasiado grande (máx. ~200MB / 20+ min)`;
  }
  if (file.size < 100) {
    return `${file.name} parece vacío`;
  }
  return null;
}

/** Rough duration estimate from size (kbps). Conservative for progress UI only. */
export function estimateDurationSec(file) {
  const kbps = 64;
  return Math.max(1, Math.round((file.size * 8) / (kbps * 1000)));
}

/**
 * Transcribe a single audio/video file via Deepgram pre-recorded API.
 * @returns {Promise<{ transcript: string, duration?: number }>}
 */
export async function transcribeAudioFile(file, { language = "es", signal } = {}) {
  const key = await getDeepgramKey();
  const params = new URLSearchParams({
    model: "nova-2",
    language,
    punctuate: "true",
    smart_format: "true",
    utterances: "false",
  });

  const res = await fetchWithTimeout(`https://api.deepgram.com/v1/listen?${params}`, {
    method: "POST",
    headers: {
      Authorization: `Token ${key}`,
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
    signal,
  }, TRANSCRIBE_TIMEOUT_MS);

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    if (res.status === 401 || res.status === 403) {
      cachedKey = null;
      throw new Error("Auth Deepgram falló. Revisá DEEPGRAM_API_KEY en Vercel.");
    }
    throw new Error(`Deepgram ${res.status}: ${body.slice(0, 160) || res.statusText}`);
  }

  const data = await res.json();
  const alt = data?.results?.channels?.[0]?.alternatives?.[0];
  const transcript = (alt?.transcript || "").trim();
  const duration = data?.metadata?.duration;
  return { transcript, duration };
}

export function titleFromTranscript(text, fallback = "Nota de voz") {
  const line = (text || "")
    .split(/\n/)
    .map((l) => l.trim())
    .find(Boolean);
  if (!line) return fallback;
  return line.length > 60 ? `${line.slice(0, 57)}…` : line;
}
